import {
  CategoryMappingEndpoint,
  ErpCategoryEndpoint,
  RetailerLocationEndpoint,
  UserEndpoint,
} from "@api/endpoints";
import { RetailerEndpoint } from "@api/endpoints/RetailerEndpoint";
import { ApiCategory, ApiRetailer } from "@api/interfaces";
import { UserType } from "@shared/enums";
import { SharedConfig } from "@shared/SharedConfig";
import { QueryUtil } from "../Query";
import { Client } from "./Client";

export class RetailerApi {
  static create (
    request: RetailerEndpoint.Create.Request,
  ): Promise<RetailerEndpoint.Create.Response> {
    return Client('retailer', {
      body: request,
      method: 'POST',
    });
  }

  static delete (id: string): Promise<void> {
    return Client(`retailer/${id}`, { method: 'DELETE' });
  }

  static getCategoryMappings (
    id: string,
  ): Promise<CategoryMappingEndpoint.ListForRetailer.Response> {
    return Client(`retailer/${id}/category-mappings`);
  }

  static getEarliestSyncedTransactionDate (
    id: string,
  ): Promise<RetailerEndpoint.GetEarliestSyncedTransactionDate.Response> {
    return Client(`retailer/${id}/earliest-synced-transaction-date`);
  }

  static initializeFarmerRewards (
    id: string,
  ): Promise<void> {
    return Client(`retailer/${id}/initialize-farmer-rewards`, { method: 'POST' });
  }

  static list (queries: RetailerEndpoint.List.Query, usePost?: boolean):
    Promise<RetailerEndpoint.List.Response> {
    if (usePost) {
      return Client('retailer/list', { body: queries, method: 'POST' });
    }
    const query = QueryUtil.stringify(queries);
    return Client(`retailer?${query}`);
  }

  static update (
    id: string,
    request: RetailerEndpoint.Update.Request,
  ): Promise<ApiRetailer> {
    return Client(`retailer/${id}`, {
      body: request,
      method: 'PATCH',
    });
  }

  static applyHistoricalCutoff (id: string): Promise<void> {
    return Client(`retailer/${id}/apply-historical-cutoff`, {
      method: 'POST',
    });
  }

  static getAssociatedFarmers (id: string, query: RetailerEndpoint.GetAssociatedFarmers.Query):
    Promise<RetailerEndpoint.GetAssociatedFarmers.Response> {
    return Client(`retailer/${id}/associated-farmers?${QueryUtil.stringify(query)}`);
  }

  static listCategories (
    id: string,
    query?: RetailerEndpoint.GetCategories.Query,
  ): Promise<ApiCategory[]> {
    const queryString = query ? QueryUtil.stringify(query) : '';
    return Client(`retailer/${id}/categories?${queryString}`);
  }

  static get (id: string): Promise<ApiRetailer> {
    return Client(`retailer/${id}`);
  }

  static getForDashboard (id: string): Promise<ApiRetailer> {
    return Client(`retailer/${id}/dashboard`);
  }

  static getLocations (id: string, queries: RetailerEndpoint.List.Query):
    Promise<RetailerEndpoint.LocationList.Response> {
    const query = QueryUtil.stringify(queries);
    return Client(`retailer/${id}/location?${query}`);
  }

  static addRetailerLocation (request: RetailerLocationEndpoint.Create.Request):
    Promise<RetailerLocationEndpoint.Create.Request> {
    return Client(`location`, {
      body: request,
      method: 'POST',
    });
  }

  static addRetailerMember (request: UserEndpoint.Create.Request):
    Promise<UserEndpoint.Create.Response> {

    const updatedRequest = { ...request };

    if (updatedRequest?.userType === UserType.RetailerAdmin) {
      delete updatedRequest.locationId;
    }

    return Client(`admin/users`, {
      body: updatedRequest,
      method: 'POST',
    });
  }

  static exportChurnCustomers (
    id: string,
    query?: RetailerEndpoint.ListTierCustomers.Query,
  ): Promise<string> {
    return Client(`retailer/${id}/churn-customers-export`, { body: query, method: 'POST' });
  }

  static exportChurnCustomersForRetailer (id: string): Promise<string> {
    return Client(`retailer/${id}/churn-data-export`, { method: 'POST' });
  }


  static getRetailerMembers (id: string, search?: string):
    Promise<RetailerEndpoint.MemberList.Response> {
    if (search) {
      return Client(`retailer/${id}/retailer-admin?search=${search}&limit=${SharedConfig.maxPageLimit}`);
    }
    return Client(`retailer/${id}/retailer-admin?limit=${SharedConfig.maxPageLimit}`);
  }

  static getLocationMembers (id: string, search?: string):
    Promise<RetailerLocationEndpoint.SalespersonList.Response> {
    if (search) {
      return Client(`location/${id}/salesperson?search=${search}&limit=${SharedConfig.maxPageLimit}`);
    }
    return Client(`location/${id}/salesperson?limit=${SharedConfig.maxPageLimit}`);
  }

  static getPointsKPIs (
    id: string,
    query: RetailerEndpoint.GetPointsKPIs.Query,
  ): Promise<RetailerEndpoint.GetPointsKPIs.Response> {
    const queryString = QueryUtil.stringify(query);
    return Client(`retailer/${id}/points-kpis?${queryString}`);
  }

  static getPointsKPIsByCustomer (
    id: string,
    body: RetailerEndpoint.GetPointsKPIsByCustomer.Request,
  ): Promise<RetailerEndpoint.GetPointsKPIsByCustomer.Response> {
    return Client(`retailer/${id}/points-kpis-by-customer`, {
      body,
      method: 'POST',
    });
  }

  static getPointsOverview (
    id: string,
    body?: RetailerEndpoint.GetPointsOverview.Request,
  ): Promise<RetailerEndpoint.GetPointsOverview.Response> {
    return Client(`retailer/${id}/points-overview`, {
      body,
      method: 'POST',
    });
  }

  static getPointsOverviewFilterOptions (
    id: string,
    query?: RetailerEndpoint.GetPointsOverviewFilterOptions.Query,
  ): Promise<RetailerEndpoint.GetPointsOverviewFilterOptions.Response> {
    if (query) {
      const queryString = QueryUtil.stringify(query);
      return Client(`retailer/${id}/points-overview-filters?${queryString}`);
    } else {
      return Client(`retailer/${id}/points-overview-filters`);
    }
  }

  static getRetailerLocationById (id: string):
    Promise<RetailerLocationEndpoint.LocationById.Response> {
    return Client(`location/${id}`);
  }

  static getCustomerCountsPerTier (
    id: string,
    programId?: string,
  ): Promise<RetailerEndpoint.GetCustomerCountsPerTier.Response> {
    const query = QueryUtil.stringify(programId ? { programId } : {});
    return Client(`retailer/${id}/tier-customer-counts?${query}`);
  }

  static getCustomerChurnPerStatus (
    id: string,
  ): Promise<RetailerEndpoint.GetCustomerChurnPerStatus.Response> {
    return Client(`retailer/${id}/churn-customer-counts`);
  }

  static hasTierOverrideOrders (id: string): Promise<boolean> {
    return Client(`retailer/${id}/has-tier-override-orders`);
  }

  static listErpCategories (
    id: string,
  ): Promise<ErpCategoryEndpoint.ListForRetailer.Response> {
    return Client(`retailer/${id}/erp-categories`);
  }

  static listPrimaryCustomers (
    id: string,
    query?: RetailerEndpoint.ListPrimaryCustomers.Query,
  ): Promise<RetailerEndpoint.ListPrimaryCustomers.Response> {
    if (query) {
      return Client(`retailer/${id}/primary-customers/?${QueryUtil.stringify(query)}`);
    }
    return Client(`retailer/${id}/primary-customers`);
  }

  static listCustomerOverview (
    id: string,
    body: RetailerEndpoint.GetCustomerOverview.Request,
  ): Promise<RetailerEndpoint.GetCustomerOverview.Response> {
    return Client(`retailer/${id}/customer-overview`, { body, method: 'POST' });
  }

  static listTierCustomers (
    id: string,
    query?: RetailerEndpoint.ListTierCustomers.Query,
  ): Promise<RetailerEndpoint.ListTierCustomers.Response> {
    return Client(`retailer/${id}/tier-customers`, { body: query, method: 'POST' });
  }

  static listTransactionPointsHistory (
    id: string,
    body: RetailerEndpoint.GetTransactionPointsHistory.Request,
  ): Promise<RetailerEndpoint.GetTransactionPointsHistory.Response> {
    return Client(`retailer/${id}/transaction-points-history`, { body, method: 'POST' });
  }

  static processTierOverrides (id: string) {
    return Client(`retailer/${id}/process-tier-overrides`, {
      method: 'POST',
    });
  }

  static updateRetailerLocation (id: string, request: RetailerLocationEndpoint.Update.Request):
    Promise<void> {
    return Client(`location/${id}`, {
      body: request,
      method: 'PATCH',
    });
  }

  static updateMember
  (id: string, request: UserEndpoint.Update.Request, isLocation?: boolean): Promise<void> {

    if (!isLocation) {
      delete request.locationId;
    }

    return Client(`users/${id}`, {
      body: request,
      method: 'PATCH',
    });
  }

  static deleteMember (id: string): Promise<void> {
    return Client(`users/${id}`, {
      method: 'DELETE',
    });
  }

  static getActiveRetailerLists (
    queries: RetailerEndpoint.RetailerList.Query,
  ): Promise<RetailerEndpoint.RetailerList.Response> {
    const query = QueryUtil.stringify(queries);
    return Client(`retailer/active/list?${query}`);
  }

  static getLoyaltyProgramMetadata (
    subdomain: string,
  ): Promise<RetailerEndpoint.LoyaltyProgramMetadata.Response> {
    return Client(`retailer/${subdomain}/metadata`);
  }

  static updateLocation (id: string, request: RetailerLocationEndpoint.Update.Request):
    Promise<void> {
    return Client(`location/${id}`, {
      body: request,
      method: 'PATCH',
    });
  }

  static processRewards (retailerId: string): Promise<void> {
    return Client(`retailer/${retailerId}/process-rewards`, {
      method: 'POST',
    });
  }
}
